const Parser = require("./Parser");
const Font = require("../util/Font");
const CanvasText = require("../util/CanvasText");
const {drawArrow} = require("../util/ArrowDraw");
const ColorUtil = require("../util/ColorUtil");
const colors = ColorUtil.getDiagramColors();
const _ = require("lodash");

const {computeText: computeOperatorText} = require("./Operators");
const {
	scaleFont,
	withCommas,
	parseCommaNumber,
	drawCurlyBrace,
	drawLongDivisionBracket,
	getDigitWidth,
	getLineWidth,
	getSpaceWidth,
} = require("./util/RenderHelpers");

function calculateStepBreakdownInfo(
	rc,
	fontInfo,
	props,
	steps,
	divisor,
	hasSideBar
) {
	if (!hasSideBar) {
		return {stepBreakdowns: [], stepBreakdownSizes: []};
	}

	const stepBreakdowns = [];
	const stepBreakdownSizes = [];

	// For the subtraction steps (with even indices), we need to break them down.
	// For example: 128 / 4, the first step is -120, which we break down into 30 x 4.
	for (let i = 0; i < steps.length; i += 2) {
		const stepText = steps[i].text || steps[i].children[0].text;
		// Get step total by parsing the step text as number
		const stepTotal = parseCommaNumber(stepText);
		// Divide this by the divisor
		const stepPlaceValue = stepTotal / divisor;
		// Format the result as a product
		const stepBreakdown = {
			type: "wrapping",
			children: [
				{
					type: "bold",
					children: [
						{
							type: "text",
							text: `${stepPlaceValue} `,
						},
					],
				},
				{
					type: "math",
					children: [
						{
							type: "operator",
							text: "*",
							isUnary: true,
						},
					],
				},
				{
					type: "text",
					text: ` ${divisor}`,
				},
			],
		};
		const stepBreakdownSize = rc.measureNode(
			rc,
			fontInfo,
			props,
			stepBreakdown
		);
		stepBreakdowns.push(stepBreakdown);
		stepBreakdownSizes.push(stepBreakdownSize);
	}

	return {stepBreakdowns, stepBreakdownSizes};
}

module.exports = {
	longDivisionBracket: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {lineWidth, space, top, width, bottom, leftPadding} = measure;
			context.save();
			context.lineWidth = lineWidth;
			drawLongDivisionBracket(context, x + space, y - top, width, top + bottom);
			rc.drawNode(rc, x + space + leftPadding, y, node.children[0]);
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const lineWidth = getLineWidth(rc, fontInfo);
			context.save();
			context.font = Font.fromInfo(fontInfo);
			const innerMeasure = rc.measureNode(
				rc,
				fontInfo,
				props,
				node.children[0]
			);
			const space = getSpaceWidth(rc, fontInfo);
			context.restore();
			return {
				width: innerMeasure.width + space * 3.5,
				top: innerMeasure.top + lineWidth * 2,
				bottom: innerMeasure.bottom,
				fontInfo,
				lineWidth,
				leftPadding: getSpaceWidth(rc, fontInfo) * 2,
				topPadding: lineWidth * 1.5,
				space,
			};
		},
	},
	longDivision: {
		draw(rc, x, y, node, measure) {
			const [
				dividendMathNode,
				divisorMathNode,
				partialQuotientsListNode, // eslint-disable-line no-unused-vars
				quotientMathNode,
				quotientStepsListNode, // eslint-disable-line no-unused-vars
			] = node.children;
			const context = rc.context;
			const dividend = parseCommaNumber(dividendMathNode.children[0]);
			const divisor = parseCommaNumber(divisorMathNode.children[0]);
			const quotientSum = parseCommaNumber(quotientMathNode.children[0]);
			const {
				mainHeight,
				fontInfo,
				mainWidth,
				divBracketYBuffer,
				displayQuotientSteps,
				nudgeSize,
				quotients,
				quotientSizes,
				quotientIntegerSizes,
				maxQuotientHeight,
				quotientSumSize,
				quotientSumIntegerSize,
				divisorSize,
				dividendSize,
				dividendIntegerSize,
				plusNode,
				plusSize,
				stepSizes,
				stepIntegerSizes,
				steps,
				stepBreakdowns,
				stepBreakdownSizes,
				stepBreakdownMargin,
			} = measure;
			const topY = y - mainHeight / 2; //top
			const leftX = x;
			y = topY + (nudgeSize.top + nudgeSize.bottom) * node.nudgeNum;
			const lineWidth = getLineWidth(rc, fontInfo);
			const space = getSpaceWidth(rc, fontInfo);
			const decimalAlign =
				x + divisorSize.width + 3 * space + dividendIntegerSize.width;
			const rightAlign = x + mainWidth - space;
			const yBuffer = divBracketYBuffer;
			// Reusable variables for positioning child nodes as they are drawn
			let childX;
			let childY;

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;

			// Drawing the quotient(s)
			if (displayQuotientSteps && node.quotientDisplayType === "list") {
				//Drawing the quotients as a list
				for (let i = 0; i < quotients.length; i++) {
					childX = decimalAlign - quotientIntegerSizes[i].width;
					childY = y + (quotientSizes[i].top + quotientSizes[i].bottom) / 2;
					rc.drawNode(rc, childX, childY, quotients[i]);
					y += quotientSizes[i].top + quotientSizes[i].bottom;
				}

				if (!node.noSum) {
					//Drawing the bracket
					const radius = drawCurlyBrace(
						rc,
						y - topY - 5 * lineWidth,
						x + mainWidth + lineWidth,
						topY + 3 * lineWidth
					);
					//Drawing the quotient sum
					childX = x + mainWidth + 3 * radius;
					childY = (topY + y) / 2;
					rc.drawNode(rc, childX, childY, quotientSum);
				}
			} else if (node.quotientDisplayType === "plus") {
				//Drawing the quotients as a sum
				childX = rightAlign - quotientIntegerSizes[quotients.length - 1].width;
				for (let i = quotients.length - 1; i > 0; i--) {
					childY = y + quotientSizes[i].top;
					rc.drawNode(rc, childX, childY, quotients[i]);
					childX += quotientSizes[i].width;
					rc.drawNode(rc, childX, childY, plusNode);
					childX += plusSize.width;
				}
				childY = y + quotientSizes[0].top;
				rc.drawNode(rc, childX, childY, quotients[0]);
				y += maxQuotientHeight;
			} else if (node.quotientDisplayType === "none") {
				// Don't draw the total quotient
				y += quotientSumSize.top + quotientSumSize.bottom;
			} else {
				// Draw the total quotient
				childX = decimalAlign - quotientSumIntegerSize.width;
				childY = y + (quotientSumSize.top + quotientSumSize.bottom) / 2;
				rc.drawNode(rc, childX, childY, quotientSum);
				y += quotientSumSize.top + quotientSumSize.bottom;
			}

			// Drawing the divisor
			y += lineWidth;
			rc.drawNode(
				rc,
				x,
				y + (divisorSize.top + divisorSize.bottom) / 2 + yBuffer + lineWidth,
				divisor
			);

			// Save the y position of the top of the bracket for side bar drawing
			const bracketTop = y;

			// Drawing the long division sign
			x += divisorSize.width;
			const divSignHeight =
				Math.max(
					divisorSize.top + divisorSize.bottom,
					dividendSize.top + dividendSize.bottom
				) +
				2 * yBuffer;
			drawLongDivisionBracket(
				context,
				x + space,
				y,
				mainWidth - divisorSize.width - space,
				divSignHeight
			);

			// //Drawing the dividend
			childX = decimalAlign - dividendIntegerSize.width;
			childY =
				y + (dividendSize.top + dividendSize.bottom) / 2 + yBuffer + lineWidth;
			rc.drawNode(rc, childX, childY, dividend);

			y += divSignHeight + 2 * lineWidth;

			// //Drawing the steps
			const minus = computeOperatorText("-");
			const subWidth = context.measureText(minus).width;
			const numberOfStepPairsToDraw = Math.max(
				quotients.length,
				Math.ceil(steps.length / 2)
			);
			for (let i = 0; i < numberOfStepPairsToDraw; i++) {
				if (steps.length <= 2 * i) {
					break;
				}
				// Drawing what's being subtracted
				// Minus sign
				context.font = Font.fromInfo(measure.fontInfo);
				CanvasText.drawMiddleText(
					context,
					minus,
					decimalAlign - stepIntegerSizes[2 * i].width - subWidth,
					y + stepSizes[2 * i].top
				);

				// Quantity being subtracted
				childX = decimalAlign - stepIntegerSizes[2 * i].width;
				childY = y + (stepSizes[2 * i].top + stepSizes[2 * i].bottom) / 2;
				rc.drawNode(rc, childX, childY, steps[2 * i]);

				const maxStepBreakdownWidth = Math.max(
					...stepBreakdownSizes.map(stepBreakdown => stepBreakdown.width)
				);

				// Step breakdown
				if (!node.noStepBreakdown && stepBreakdowns[i]) {
					rc.drawNode(
						rc,
						leftX +
							mainWidth +
							stepBreakdownMargin +
							(maxStepBreakdownWidth - stepBreakdownSizes[i].width),
						childY - stepBreakdownSizes[i].height / 2,
						stepBreakdowns[i]
					);
				}

				// Line below the quantity being subtracted
				y += stepSizes[2 * i].top + stepSizes[2 * i].bottom + 2 * lineWidth;
				context.moveTo(
					decimalAlign - stepIntegerSizes[2 * i].width - subWidth,
					y
				);
				context.lineTo(
					decimalAlign + stepSizes[2 * i].width - stepIntegerSizes[2 * i].width,
					y
				);
				context.stroke();
				y += 2 * lineWidth;
				if (steps.length <= 2 * i + 1) {
					break;
				}
				//Drawing the remainder
				// If remainder is 0, it has to be right aligned with the previous step rather than aligned with the decimal.
				childX = decimalAlign - stepIntegerSizes[2 * i + 1].width;
				const stepText =
					steps[2 * i + 1].text || _.last(steps[2 * i + 1].children).text;
				if (stepText === "0") {
					childX += stepSizes[2 * i].width - stepIntegerSizes[2 * i].width;
				}
				childY =
					y + (stepSizes[2 * i + 1].top + stepSizes[2 * i + 1].bottom) / 2;
				rc.drawNode(rc, childX, childY, steps[2 * i + 1]);

				y +=
					stepSizes[2 * i + 1].top +
					stepSizes[2 * i + 1].bottom +
					3 * lineWidth;
			}

			// Drawing the side bar
			if (node.sideBar) {
				context.save();
				context.bp();
				context.strokeStyle = colors.black;
				context.lineCap = "round";
				context.moveTo(leftX + mainWidth, bracketTop + lineWidth);
				context.lineTo(leftX + mainWidth, topY + mainHeight);
				context.stroke();
				context.restore();
			}

			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const [
				dividendMathNode,
				divisorMathNode,
				partialQuotientsListNode,
				quotientMathNode,
				quotientStepsListNode,
			] = node.children;
			const yPadding = fontInfo.size / 2;
			const dividendSize = rc.measureNode(
				rc,
				fontInfo,
				props,
				dividendMathNode
			);
			const dividendIntegerSize = rc.measureNode(rc, fontInfo, props, {
				type: "text",
				text: withCommas(
					parseInt(parseCommaNumber(dividendMathNode.children[0].text))
				),
			});
			const divisorSize = rc.measureNode(rc, fontInfo, props, divisorMathNode);
			let maxDecimalWidth = dividendSize.width - dividendIntegerSize.width;
			const getNumDecimalPlaces = decimal => {
				const decimalSplit = decimal.toString().split(".");
				return decimalSplit.length > 1 ? decimalSplit[1].length : 0;
			};
			const quotientsArray = partialQuotientsListNode.listData;
			const quotientSizes = [];
			const quotientIntegerSizes = [];
			let quotients = [];
			let q = [];
			let displayQuotientSteps = true;
			let parseResult;
			let i;
			let decimalsLength;

			if (quotientsArray.length === 0) {
				displayQuotientSteps = false;
				//Calculating the step by step quotients if there is only one quotient (the quotientSum)
				let curr = parseCommaNumber(quotientMathNode.children[0].text);
				let pow10 = 1;
				while (curr > 0) {
					if (curr % 10 !== 0) {
						q.push((curr % 10) * pow10);
					}
					curr = Math.floor(curr / 10);
					pow10 *= 10;
				}
			} else {
				q = quotientsArray;
			}
			const steps = quotientStepsListNode.listData;
			const stepSizes = [];
			const stepIntegerSizes = [];
			const divisor = parseCommaNumber(divisorMathNode.children[0].text);
			// Populate the steps and step sizes
			if (steps.length === 0) {
				//If no steps are provided, the other arguments must have been
				//numbers, so we can calculate the steps
				const dividend = parseCommaNumber(dividendMathNode.children[0].text);
				let previousStep = dividendMathNode.children[0].text;
				let remainder = dividend;
				let nextStep;
				// Rounding numbers to 10 decimals prevents repeating decimals from
				// showing up
				for (i = q.length - 1; i >= 0; i--) {
					const product = _.round(q[i] * divisor, 10);
					decimalsLength = Math.max(
						getNumDecimalPlaces(product),
						getNumDecimalPlaces(previousStep),
						getNumDecimalPlaces(q[i])
					);
					steps[2 * i + 1] = {
						type: "text",
						text: withCommas(product.toFixed(decimalsLength)),
					};
					nextStep = _.round(remainder - q[i] * divisor, 10);
					if (i > 0) {
						// Decimal places match the product of the divisor and next quotient.
						decimalsLength = Math.max(
							getNumDecimalPlaces(nextStep),
							getNumDecimalPlaces(_.round(q[i - 1] * divisor, 10)),
							getNumDecimalPlaces(q[i - 1])
						);
					} else {
						// If last step is non-zero, its decimal places should match the previous subtraction.
						decimalsLength = nextStep
							? Math.max(getNumDecimalPlaces(nextStep), decimalsLength)
							: 0;
					}
					nextStep = nextStep.toFixed(decimalsLength);
					steps[2 * i] = {
						type: "text",
						text: withCommas(nextStep),
					};
					stepSizes[2 * i] = rc.measureNode(rc, fontInfo, props, steps[2 * i]);
					stepSizes[2 * i + 1] = rc.measureNode(
						rc,
						fontInfo,
						props,
						steps[2 * i + 1]
					);
					stepIntegerSizes[2 * i] = rc.measureNode(rc, fontInfo, props, {
						type: "text",
						text: withCommas(parseInt(nextStep)),
					});
					stepIntegerSizes[2 * i + 1] = rc.measureNode(rc, fontInfo, props, {
						type: "text",
						text: withCommas(parseInt(product)),
					});
					maxDecimalWidth = Math.max(
						maxDecimalWidth,
						stepSizes[2 * i].width - stepIntegerSizes[2 * i].width,
						stepSizes[2 * i + 1].width - stepIntegerSizes[2 * i + 1].width
					);
					remainder = _.round(remainder - q[i] * divisor, 10);
					previousStep = remainder;
				}
				steps.reverse();
				stepSizes.reverse();
				stepIntegerSizes.reverse();
			} else {
				//If steps are provided, we just use them
				for (i = 0; i < steps.length; i++) {
					const stepText = steps[i].text || steps[i].children[0].text;
					stepSizes[i] = rc.measureNode(rc, fontInfo, props, steps[i]);
					stepIntegerSizes[i] = rc.measureNode(rc, fontInfo, props, {
						type: "text",
						text: withCommas(parseInt(parseCommaNumber(stepText))),
					});
					maxDecimalWidth = Math.max(
						maxDecimalWidth,
						stepSizes[i].width - stepIntegerSizes[i].width
					);
				}
			}
			// Calculate the step breakdowns to be shown to the right of the side bar if it is enabled
			const {stepBreakdowns, stepBreakdownSizes} = calculateStepBreakdownInfo(
				rc,
				fontInfo,
				props,
				steps,
				divisor,
				node.sideBar
			);
			if (quotientsArray.length === 0) {
				quotients = q.slice(0);
				for (i = 0; i < quotients.length; i++) {
					quotients[i] = {
						type: "text",
						text: withCommas(q[i]),
					};
					quotientSizes[i] = rc.measureNode(rc, fontInfo, props, quotients[i]);
					quotientIntegerSizes[i] = rc.measureNode(rc, fontInfo, props, {
						type: "text",
						text: withCommas(parseInt(q[i])),
					});
					maxDecimalWidth = Math.max(
						maxDecimalWidth,
						quotientSizes[i].width - quotientIntegerSizes[i].width
					);
				}
			} else {
				for (i = 0; i < quotientsArray.length; i++) {
					const stepIndex = steps.length - 2 - 2 * i;
					const stepText =
						steps[stepIndex].text || _.last(steps[stepIndex].children).text;
					decimalsLength = Math.max(
						getNumDecimalPlaces(quotientsArray[i]),
						getNumDecimalPlaces(stepText)
					);
					parseResult = Parser.parseMathBatex(
						withCommas(quotientsArray[i].toFixed(decimalsLength))
					);
					rc.mergeErrors(parseResult.errors);
					quotients[i] = parseResult.tree;
					quotientSizes[i] = rc.measureNode(rc, fontInfo, props, quotients[i]);
					quotientIntegerSizes[i] = rc.measureNode(rc, fontInfo, props, {
						type: "text",
						text: withCommas(parseInt(quotientsArray[i])),
					});
					maxDecimalWidth = Math.max(
						maxDecimalWidth,
						quotientSizes[i].width - quotientIntegerSizes[i].width
					);
				}
			}
			const quotientSumSize = rc.measureNode(
				rc,
				fontInfo,
				props,
				quotientMathNode
			);
			const quotientSumText =
				_.get(quotientMathNode, "children[0].text") ||
				_.sum(partialQuotientsListNode.listData) ||
				"";
			const quotientSumIntegerSize = rc.measureNode(rc, fontInfo, props, {
				type: "text",
				text: withCommas(parseInt(parseCommaNumber(quotientSumText))),
			});

			//Getting measurements
			const lineWidth = getLineWidth(rc, fontInfo);
			const mainWidth =
				divisorSize.width +
				dividendIntegerSize.width +
				maxDecimalWidth +
				getSpaceWidth(rc, fontInfo) * 4;
			let rightWidth = 0;
			let plusNode, plusSize;
			const stepBreakdownMargin = fontInfo.size * 0.5;

			// Calculate the width right of the division bracket
			if (
				displayQuotientSteps &&
				node.quotientDisplayType === "list" &&
				!node.noSum
			) {
				rightWidth = Math.max(
					rightWidth,
					9 * lineWidth + quotientSumSize.width
				);
			}
			if (node.quotientDisplayType === "plus") {
				let rightWidthFromPlus = 0;
				for (i = 1; i < quotients.length; i++) {
					plusNode = {
						type: "operator",
						text: "+",
						isUnary: false,
					};
					plusSize = rc.measureNode(rc, fontInfo, props, plusNode);
					rightWidthFromPlus += plusSize.width + quotientSizes[i].width;
				}
				rightWidth = Math.max(rightWidth, rightWidthFromPlus);
			}
			if (node.sideBar && !node.noStepBreakdown) {
				const sideBarStepBreakdownRightWidth =
					Math.max(...stepBreakdownSizes.map(s => s.width)) +
					stepBreakdownMargin;
				rightWidth = Math.max(rightWidth, sideBarStepBreakdownRightWidth);
			}

			// Calculate heights
			const divBracketYBuffer = 2 * lineWidth;
			const nudgeSize = rc.measureNode(rc, fontInfo, props, {
				type: "text",
				text: "0",
			});
			let height = node.nudgeNum * (nudgeSize.top + nudgeSize.bottom);
			let maxQuotientHeight = 0;
			for (i = 0; i < quotients.length; i++) {
				if (displayQuotientSteps && node.quotientDisplayType === "list") {
					height += quotientSizes[i].top + quotientSizes[i].bottom;
				} else {
					maxQuotientHeight = Math.max(
						maxQuotientHeight,
						quotientSizes[i].top + quotientSizes[i].bottom
					);
				}
			}
			// Calculate the height of each of the steps below the division bracket
			for (i = 0; i < steps.length; i++) {
				height += stepSizes[i].top + stepSizes[i].bottom + 3 * lineWidth;
				// For even steps, add space for the line drawn below the step
				if (i % 2 === 0) height += lineWidth;
			}
			if (node.quotientDisplayType === "plus") {
				height += maxQuotientHeight;
			}
			if (!displayQuotientSteps) {
				height += quotientSumSize.top + quotientSumSize.bottom;
			}
			height += 3 * lineWidth;
			height += Math.max(
				divisorSize.top + divisorSize.bottom,
				dividendSize.top + dividendSize.bottom
			);
			height += 2 * divBracketYBuffer;
			return {
				width: mainWidth + rightWidth,
				top: height / 2 + yPadding,
				bottom: height / 2 + yPadding,
				fontInfo,
				// extra
				mainHeight: height,
				mainWidth,
				rightWidth,
				dividendSize,
				dividendIntegerSize,
				divisorSize,
				nudgeSize,
				quotients,
				quotientSizes,
				quotientIntegerSizes,
				quotientSumSize,
				quotientSumIntegerSize,
				steps,
				stepIntegerSizes,
				stepSizes,
				stepBreakdowns,
				stepBreakdownSizes,
				stepBreakdownMargin,
				displayQuotientSteps,
				divBracketYBuffer,
				plusNode,
				plusSize,
				maxQuotientHeight,
			};
		},
	},
	longMultiplication: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {
				fontInfo,
				width,
				multiplicandSizes,
				multWidth,
				spaceWidth,
				timesX,
				plusX,
				labelSizes,
				labelFontInfo,
				resultSizes,
				resultWidth,
				sumSize,
				startYOffset,
				arrowLength,
				maxLabelWidth,
			} = measure;
			const lineWidth = getLineWidth(rc, fontInfo);
			const rightAlign = x + width - spaceWidth;
			let i;

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;

			y -= startYOffset;
			//drawing the multiplicands
			for (i = 0; i < multiplicandSizes.length; i++) {
				y += multiplicandSizes[i].top;
				rc.drawNode(
					rc,
					rightAlign - multiplicandSizes[i].width,
					y,
					node.children[0].listData[i]
				);
				y += multiplicandSizes[i].bottom + (node.isDigits ? 3 * lineWidth : 0);
			}
			const lastMultiplicandSize =
				multiplicandSizes[multiplicandSizes.length - 1];
			context.font = Font.fromInfo(measure.fontInfo);
			CanvasText.drawMiddleText(
				context,
				"\u00D7",
				rightAlign - timesX,
				y - lastMultiplicandSize.bottom - (node.isDigits ? 3 * lineWidth : 0)
			);
			y += 1 * lineWidth;
			context
				.bp()
				.M(x + width - multWidth, y)
				.L(x + width, y)
				.stroke();
			y += (node.isDigits ? 4 : 2) * lineWidth;
			let yArrow = y;
			//drawing the results
			for (i = 0; i < resultSizes.length; i++) {
				y += resultSizes[i].top;
				rc.drawNode(
					rc,
					rightAlign - resultSizes[i].width,
					y,
					node.children[1].listData[i]
				);
				y += resultSizes[i].bottom + (node.isDigits ? 3 * lineWidth : 0);
			}
			//drawing the labels
			if (node.children[3].listData.length) {
				context.save();
				context.font = Font.fromInfo(labelFontInfo);
				context.strokeStyle = colors.defaultBlack;
				for (i = 0; i < resultSizes.length && i < labelSizes.length; i++) {
					yArrow += resultSizes[i].top;
					rc.drawNode(
						rc,
						x + maxLabelWidth - labelSizes[i].width,
						yArrow,
						node.children[3].listData[i]
					);
					drawArrow(rc.context, {
						x1: x + maxLabelWidth + spaceWidth,
						y1: yArrow,
						x2: x + maxLabelWidth + spaceWidth + arrowLength,
						y2: yArrow,
						headLength: arrowLength / 5,
						headHeight: (arrowLength * 2) / 15,
						lineWidth: 1.5 * lineWidth,
					});
					yArrow += resultSizes[i].bottom + (node.isDigits ? 3 * lineWidth : 0);
				}
				context.restore();
			}
			//drawing the sum
			if (node.isSum) {
				const lastResultSize = resultSizes[resultSizes.length - 1];
				context.font = Font.fromInfo(measure.fontInfo);
				CanvasText.drawMiddleText(
					context,
					"+",
					rightAlign - plusX,
					y - lastResultSize.bottom - (node.isDigits ? 3 * lineWidth : 0)
				);
				y += 1 * lineWidth;
				context
					.bp()
					.M(x + width - resultWidth, y)
					.L(x + width, y)
					.stroke();
				y += (node.isDigits ? 4 : 2) * lineWidth;
				rc.drawNode(
					rc,
					rightAlign - sumSize.width,
					y + sumSize.top,
					node.children[2]
				);
			}
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const multiplicandArray = node.children[0].listData;
			const resultArray = node.children[1].listData;
			const labelArray = node.children[3].listData;
			const labelFontInfo = scaleFont(fontInfo, 1);
			const yPadding = fontInfo.size / 2;

			const multiplicandSizes = multiplicandArray.map(m => {
				return rc.measureNode(rc, fontInfo, props, m);
			});
			const resultSizes = resultArray.map(r => {
				return rc.measureNode(rc, fontInfo, props, r);
			});
			const labelSizes = labelArray.map(l => {
				return rc.measureNode(rc, labelFontInfo, props, l);
			});
			const sumSize = rc.measureNode(rc, fontInfo, props, node.children[2]);

			context.save();
			context.font = Font.fromInfo(fontInfo);
			const plusWidth = context.measureText("+").width;
			const timesWidth = context.measureText("\u00D7").width;
			const spaceWidth = context.measureText(" ").width;
			const lineWidth = getLineWidth(rc, fontInfo);
			let height = 0;
			//measuring the labels
			const maxLabelWidth = _.reduce(
				labelSizes,
				(max, l) => {
					return Math.max(max, l.width);
				},
				0
			);
			const arrowLength = getDigitWidth(rc, fontInfo) * 4;
			//measuring the multiplicands
			let maxMultWidth = 0;
			multiplicandSizes.forEach(ms => {
				height += ms.top + ms.bottom;
				maxMultWidth = Math.max(ms.width, maxMultWidth);
			});
			const lastMultiplicandLargest =
				maxMultWidth === multiplicandSizes[multiplicandSizes.length - 1].width;
			const lastMultiplicandWidth =
				multiplicandSizes[multiplicandSizes.length - 1].width;
			if (node.isDigits) {
				maxMultWidth = Math.max(
					lastMultiplicandWidth +
						timesWidth * (lastMultiplicandWidth ? 1.25 : 1),
					maxMultWidth
				);
			} else {
				maxMultWidth = Math.max(
					lastMultiplicandWidth + timesWidth,
					maxMultWidth
				);
			}
			const result1Width =
				resultSizes[0] && resultSizes[0].width ? resultSizes[0].width : 0;
			height += (node.isDigits ? 6 : 3) * lineWidth;
			//measuring the results
			let maxResultWidth = 0;
			resultSizes.forEach(rs => {
				height += rs.top + rs.bottom + (node.isDigits ? 3 * lineWidth : 0);
				maxResultWidth = Math.max(rs.width, maxResultWidth);
			});
			let resultWidth = maxResultWidth;
			//if there is a sum of results
			const lastResultWidth = resultSizes.length
				? resultSizes[resultSizes.length - 1].width
				: 0;
			if (node.isSum) {
				if (node.isDigits) {
					maxResultWidth +=
						(maxResultWidth === lastResultWidth ? 1.75 : 1.25) * plusWidth;
				} else {
					maxResultWidth = Math.max(
						lastResultWidth + plusWidth * 1.5,
						maxResultWidth
					);
				}
				height += (node.isDigits ? 6 : 3) * lineWidth;
				height += sumSize.top + sumSize.bottom;
				resultWidth = Math.max(maxResultWidth, sumSize.width) + 2 * spaceWidth;
			}
			const timesX = lastMultiplicandLargest
				? maxMultWidth + timesWidth * 0.65
				: maxMultWidth + timesWidth;
			const multWidth = Math.max(timesX, result1Width) + spaceWidth * 2;
			const width =
				Math.max(multWidth, resultWidth) +
				(labelArray.length
					? spaceWidth * 2.5 + arrowLength + maxLabelWidth
					: 0);
			const plusX = node.isDigits
				? maxResultWidth
				: lastResultWidth + plusWidth * 1.5;
			context.restore();
			return {
				width: width,
				top: height / 2 + yPadding,
				bottom: height / 2 + yPadding,
				startYOffset: height / 2,
				fontInfo,
				labelFontInfo,
				multiplicandSizes,
				resultSizes,
				labelSizes,
				sumSize,
				multWidth,
				resultWidth,
				spaceWidth,
				timesX,
				plusX,
				maxLabelWidth,
				arrowLength,
			};
		},
	},
	longSimpleOperation: {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {fontInfo, width, addendSizes, sumSize, startYOffset} = measure;
			const lineWidth = getLineWidth(rc, fontInfo);

			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = lineWidth;

			const rightAlign = x + width - context.measureText(" ").width;
			y -= startYOffset;
			//drawing the addends
			for (let i = 0; i < addendSizes.length; i++) {
				y += addendSizes[i].top;
				rc.drawNode(
					rc,
					rightAlign - addendSizes[i].width,
					y,
					node.children[0].children[i]
				);
				y += addendSizes[i].bottom;
			}
			const lastAddendSize = addendSizes[addendSizes.length - 1];
			context.font = Font.fromInfo(measure.fontInfo);
			CanvasText.drawMiddleText(
				context,
				node.operator,
				rightAlign -
					lastAddendSize.width -
					context.measureText(node.operator).width * 1.5,
				y - lastAddendSize.bottom
			);
			y += 1 * lineWidth;
			context.moveTo(x, y);
			context.lineTo(x + width, y);
			context.stroke();
			y += 2 * lineWidth;
			rc.drawNode(
				rc,
				rightAlign - sumSize.width,
				y + sumSize.top,
				node.children[1]
			);
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const context = rc.context;
			const yPadding = fontInfo.size / 2;
			const addendSizes = [];
			let i;
			for (i = 0; i < node.children[0].children.length; i++) {
				addendSizes[i] = rc.measureNode(
					rc,
					fontInfo,
					props,
					node.children[0].children[i]
				);
			}
			const sumSize = rc.measureNode(rc, fontInfo, props, node.children[1]);

			context.save();
			context.font = Font.fromInfo(fontInfo);
			const lineWidth = getLineWidth(rc, fontInfo);
			let height = 0;
			let maxPartWidth = 0;
			for (i = 0; i < addendSizes.length; i++) {
				height += addendSizes[i].top + addendSizes[i].bottom;
				if (addendSizes[i].width > maxPartWidth) {
					maxPartWidth = addendSizes[i].width;
				}
			}
			if (
				addendSizes[addendSizes.length - 1].width +
					context.measureText(node.operator).width * 1.5 >
				maxPartWidth
			) {
				maxPartWidth =
					addendSizes[addendSizes.length - 1].width +
					context.measureText(node.operator).width * 1.5;
			}
			if (sumSize.width > maxPartWidth) {
				maxPartWidth = sumSize.width;
			}
			height += 3 * lineWidth;
			height += sumSize.top + sumSize.bottom;
			const width = maxPartWidth + 2 * context.measureText(" ").width;
			context.restore();
			return {
				width,
				top: height / 2 + yPadding,
				bottom: height / 2 + yPadding,
				startYOffset: height / 2,
				fontInfo,
				addendSizes,
				sumSize,
			};
		},
	},
};
